import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Link, type LinkProps } from '@tanstack/react-router';
import { cx } from '@/lib/cx';
import { Icon } from './Icon';
import s from './Button.module.css';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface CommonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: string;
  trailingIcon?: string;
  block?: boolean;
  children?: ReactNode;
}

const classes = (variant: ButtonVariant, size: ButtonSize, block?: boolean, extra?: string) =>
  cx(s.button, s[variant], size !== 'md' && s[size], block && s.block, extra);

const iconSize = (size: ButtonSize) => (size === 'sm' ? 16 : 18);

export interface ButtonProps extends CommonProps, ButtonHTMLAttributes<HTMLButtonElement> {
  loading?: boolean;
  /** An AI request is in flight: the landing headline's colours turn round the button in place of
   *  the spinner (web_app_design.md §2). Only on buttons that call the language model. */
  aiWorking?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    icon,
    trailingIcon,
    block,
    loading,
    aiWorking,
    children,
    className,
    disabled,
    type = 'button',
    ...rest
  },
  ref,
) {
  const busy = loading || aiWorking;
  return (
    <button
      ref={ref}
      type={type}
      className={classes(variant, size, block, cx(aiWorking && s.aiWorking, className))}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      {...rest}
    >
      {loading && !aiWorking ? <span className={s.spinner} /> : icon && <Icon name={icon} size={iconSize(size)} />}
      {children}
      {trailingIcon && <Icon name={trailingIcon} size={iconSize(size)} />}
    </button>
  );
});

/** A router link styled as a button. */
export function ButtonLink({
  variant = 'secondary',
  size = 'md',
  icon,
  trailingIcon,
  block,
  children,
  className,
  ...linkProps
}: CommonProps & LinkProps & { className?: string }) {
  return (
    <Link className={classes(variant, size, block, className)} {...linkProps}>
      {icon && <Icon name={icon} size={iconSize(size)} />}
      {children as ReactNode}
      {trailingIcon && <Icon name={trailingIcon} size={iconSize(size)} />}
    </Link>
  );
}

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: string;
  /** Required: an icon-only control must say what it does. */
  label: string;
  size?: 'sm' | 'md';
  outlined?: boolean;
  active?: boolean;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, label, size = 'md', outlined, active, className, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      aria-pressed={active ?? undefined}
      className={cx(s.iconButton, size === 'sm' && s.sm, outlined && s.outlined, active && s.active, className)}
      {...rest}
    >
      <Icon name={icon} size={size === 'sm' ? 18 : 20} />
    </button>
  );
});
